<template>
  <v-card flat>
    <v-card-title class="text-h5">
      Deforestation rate and relevance
    </v-card-title>
    <v-card-subtitle>
      This module creates a distance map of the deforestation from each of the selected variables.
      <br/>With this information, the tool calculates the annual deforestation rate and the percentage of annual deforestation by distance interval selected.
      <br/>Results can be download or printed in the results window.
    </v-card-subtitle>

    <v-card-text>
      <v-row>
        <!-- Input form -->
        <v-col sm="6">
          <v-card elevation="4" :disabled="finishedProcessing">
            <v-card-text>
              <v-form v-model="isValid">
                <h3>Select the variables to be included in the analysis</h3>
                <p class="text-body-1">Default global datasets for the most common variables used are able. Nevertheless, the user can upload its own dataset and additional variables.<br/>
                User have to indicate the distance interval to be applied.
                </p>

                <p class="text-body-1 mt-4">Recommended Variables:</p>
                <v-switch class="mt-0" v-model="forestEnabled" label="Forest Edge 2"></v-switch>
                <v-switch class="mt-0" v-model="deforestationEnabled" label="Deforestation 1-2"></v-switch>

                <p class="text-body-1">Most common variables:</p>

                <v-row v-for="(v, i) in variables" :key="i">
                  <v-col sm="4">
                    <v-switch class="mt-0" v-model="variablesEnabled[i]" :label="v" hide-details/>
                  </v-col>
                  <v-fade-transition>
                    <v-col sm="3" v-show="variablesEnabled[i]">
                      <v-checkbox v-model="variablesUseCustom[i]" class="mt-0" label="Use custom layer" outlined dense hide-details/>
                    </v-col>
                  </v-fade-transition>
                  <v-fade-transition>
                    <v-col v-show="variablesUseCustom[i]">
                      <v-select v-model="customVariables[i]" :items="variableLayers" item-text="NOMBRE" return-object label="Custom variable" prepend-icon="mdi-file" outlined dense hide-details/>
                    </v-col>
                  </v-fade-transition>
                </v-row>

                <v-row no-gutters class="mt-8">
                  <v-col sm="8">
                    <v-select v-model="userDefVariables" :items="variableLayers" item-text="NOMBRE" return-object label="User variables to predict deforestation" multiple prepend-icon="mdi-file" outlined dense/>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col sm="8">
                    <v-select v-model="interval" :items="analysisIntervalValues" label="Interval for which the analysis will be performed" prepend-icon="mdi-clock" outlined dense :rules="[globalRules.required]"/>
                  </v-col>
                </v-row>
              </v-form>

              <v-btn width="150" color="primary" :disabled="!isValid || !variablesValid" :loading="isInitProcessing" @click="process">Process</v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Results -->
        <v-col>
          <v-card elevation="4">
            <v-card-title>
              Results
            </v-card-title>

            <v-card-text v-show="mod2VariablesProximity.length > 0 && graphicsData">
              <v-tabs ref="tabs" v-model="resultSelected" background-color="primary" slider-color="red darken-1" grow>
                <v-tab class="white--text" :key="0">Files</v-tab>
                <v-tab class="white--text" :key="1">Graphics</v-tab>
              </v-tabs>

              <v-tabs-items v-model="resultSelected">
                <v-tab-item :key="0">
                  <vx-result-file-list class="mt-2" :fileList="mod2VariablesProximity"/>
                </v-tab-item>

                <v-tab-item :key="1">
                  <vx-graphics :graphicsData="graphicsData"/>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>

            <v-card-actions class="px-4">
              <v-btn color="primary" outlined @click="downloadFile(csvFile)" :disabled="!csvFile">
                <v-icon left>mdi-download</v-icon>
                Download CSV
              </v-btn>

              <!-- <v-btn color="primary" outlined @click="viewAllFilesMap" :disabled="mod2VariablesProximity.length == 0">
                <v-icon left>mdi-eye</v-icon>
                View on map
              </v-btn> -->
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import api from '../../api'
import Graphics from './Graphics.vue'
import ResultFileList from './ResultFileList.vue'

export default {
  name: 'Module2',

  props: {
    region: Object,
    currentProcess: String
  },

  components: {
    'vx-result-file-list': ResultFileList,
    'vx-graphics': Graphics
  },

  data: () => ({
    isValid: false,
    analysisIntervalValues: [50, 100, 125, 200, 250, 500, 625, 1000, 1250, 5000],
    variables: ['Population', 'Water cover', 'Roads'],
    forestEnabled: false,
    deforestationEnabled: false,
    variablesEnabled: [false, false, false],
    variablesUseCustom: [false, false, false],
    customVariables: [null, null, null],

    // Periodic checking
    isInitProcessing: false,
    isProcessing: false,
    intervalCall: null,
    finishedProcessing: false,

    // Inputs
    globalDefVariables: null,
    userDefVariables: null,
    interval: null,

    // Outputs
    resultSelected: 0,
    csvFile: null,
    mod2Variables: [],
    // Graphics
    graphicsData: null
  }),

  watch: {
    variablesEnabled () {
      for (let i = 0; i < this.variablesEnabled.length; i++) {
        let isEnabled = this.variablesEnabled[i]

        if (!isEnabled) {
          this.variablesUseCustom[i] = false
        }
      }
    },

    isProcessing (val) {
      this.isInitProcessing = val

      if (val && !this.intervalCall) {
        this.intervalCall = setInterval(() => {
          api.process.getResults(this.currentProcess, 2).then(response => {
            if (response.data.error) {
              this.stopProcessing()

              console.error(response.data.error)
              this.$toastr.e(response.data.message)
            } else {
              if (response.data.finished) {
                // CSV Graphics
                this.updateGraphics(response.data.results.csvGraphics)

                // CSV Zip
                this.csvFile = response.data.results.csvZip

                // File list
                this.mod2Variables = response.data.results.mod2Variables

                // Save results in store
                this.setMod2Results({
                  graphicsData: this.graphicsData,
                  csvFile: this.csvFile,
                  mod2Files: this.mod2VariablesProximity
                })

                this.$toastr.s('Module 2 succesfully processed')

                // Ready for module 3
                this.finishedProcessing = true
                this.$emit('onProcessed', {
                  mod2Variables: this.mod2Variables
                })

                this.stopProcessing()
              } else {
                // Not finished
              }
            }
          }).catch(error => {
            this.stopProcessing()

            console.error(error)
            this.$toastr.e('Error processing module 2')
          }).finally(() => {
            // Nothing
          })
        }, 10 * 1000)
      } else {
        clearInterval(this.intervalCall)
        this.intervalCall = null
      }
    }
  },

  computed: {
    variableLayers () {
      return this.$store.getters['file/filesByRegionAndType'](this.region, 2) // 2 == Deforestation variable
    },
    variablesValid () {
      // let atLeastOne = this.variablesEnabled.reduce((a, b) => a || b)

      let variablesValid = true
      for (let i = 0; i < this.variablesEnabled.length; i++) {
        let isCustomVarValid = !this.variablesUseCustom[i] || (this.variablesUseCustom[i] && !!this.customVariables[i]) // Usa global o usa custom y la ha seleccionado
        let isVarValid = !this.variablesEnabled[i] || (this.variablesEnabled[i] && isCustomVarValid)
        variablesValid = variablesValid && isVarValid
      }
      return /* atLeastOne && */ variablesValid
    },
    mod2VariablesProximity () {
      return this.mod2Variables.map(x => x.ProximityFile)
    }
  },

  methods: {
    ...mapActions('app', [
      'setIsProcessing'
    ]),

    ...mapActions('currentProcess', [
      'setMod2Results'
    ]),

    ...mapActions('file', [
      'downloadFile'
    ]),

    stopProcessing () {
      this.isProcessing = false
      clearInterval(this.intervalCall)
      this.intervalCall = null

      this.setIsProcessing(false)
    },

    reset () {
      this.finishedProcessing = false

      this.isValid = false
      this.forestEnabled = false
      this.deforestationEnabled = false
      this.variablesEnabled = [false, false, false]
      this.variablesUseCustom = [false, false, false]
      this.customVariables = [null, null, null]

      // Inputs
      this.globalDefVariables = null
      this.userDefVariables = null
      this.interval = null

      this.resultSelected = 0
      this.csvFile = null
      this.mod2Variables = []

      this.graphicsData = null
    },

    async process () {
      let data = {
        processId: this.currentProcess,
        forestEnabled: this.forestEnabled,
        deforestationEnabled: this.deforestationEnabled,
        variablesEnabled: this.variablesEnabled,
        variablesUseCustom: this.variablesUseCustom,
        customVariables: this.customVariables,
        userDefVariables: this.userDefVariables,
        interval: this.interval
      }

      try {
        this.setIsProcessing(true)
        this.isInitProcessing = true

        await api.process.module2(data)

        this.isProcessing = true
        this.$toastr.s('Module 2 starts processing...')
      } catch (error) {
        this.isInitProcessing = false

        console.error(error)
        this.$toastr.e('Error initializing module 2')
      }
    },

    updateGraphics (gd) {
      let varList = gd.variables.map(x => x.split('_'))
      for (let i = 0; i < varList.length; i++) {
        let j = varList[i].indexOf('processed')
        if (j !== -1) {
          varList[i].splice(j, 1)
        }
        varList[i].pop()
      }

      this.graphicsData = {
        categories: varList.map(x => x.join(' ')),
        annualData: gd.annual,
        accumulateData: gd.accumulate
      }
    }

    /* viewAllFilesMap () {
      let filesIds = ''

      for (let i = 0; i < this.mod2VariablesProximity.length; i++) {
        filesIds += this.mod2VariablesProximity[i].ID_FICHERO + '-'
      }

      this.$router.push({ path: 'map', query: { idFiles: filesIds } })
    } */
  }
}
</script>

<style scoped>

</style>
